import * as THREE from 'three';
import { hexDist, hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { AscuasSky } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The alarm bells (Las vías 2, docs/SAGAS.md): the clearing re-dressed as Dorn's halls deep under the mine. Every hex of
 * the clearing a swept flagstone (the roads a worn runner of darker stone), carved pillars and lamps on posts round the
 * edge, cold dark rock beyond, and far off the Anvil's warm red. Three rooms on the rim: an alcove of dressed stone, its
 * alarm frame with a bell that swings and rings while the room is awake and hangs still once its lever is down, the lever
 * (up, pulled down by the heroes' weight as the pull fills, down), a ring of studs round it that lights with the pull,
 * the guards' plinths, and a faint red ring on the floor where they strike (gone with the room).
 *
 * Draw calls: the halls (one merged mesh: flagstones, pillars, alcoves, frames, plinths, lamp posts), the lamps' glass,
 * the bells (one instanced mesh), the levers' handles (one), the pull studs (one), the guards' rings (one), a glow
 * layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['bells']>;

const STONE = ['#50545e', '#585c67', '#4a4e57', '#5f6370'];
const RUNNER = ['#3f3a36', '#47413c', '#38332f'];
const GROUT = '#24262c';
const PILLAR = '#7a7f8b';
const PILLAR_DARK = '#4a4e57';
const RUNE = '#b9c3d6';
const WOOD = ['#4a372b', '#3d2e25', '#55402f'];
const IRON = '#5c6068';
const IRON_DARK = '#35373c';
const LAMP = new THREE.Color('#ffa66b');
const LAMP_CORE = new THREE.Color('#ffe0c4');
const ANVIL = new THREE.Color('#ff3a1a');
const ALARM = new THREE.Color('#ff4f3a');
const STUD_OFF = new THREE.Color('#2a2c31');
const STUD_ON = new THREE.Color('#ff8a6e');
const STUDS = 12;
/** The lever's handle: up while the room is awake, down once pulled (radians about its pivot). */
const UP = -0.75;
const DOWN = 0.95;

export const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Repaints a vertex-coloured mesh between two colours, keeping its old light and shade. */
export const repaint = (geo: THREE.BufferGeometry, dark: THREE.Color, light: THREE.Color, lo: number, hi: number): void => {
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!col) return;
  const c = new THREE.Color();
  for (let i = 0; i < col.count; i++) {
    c.copy(dark).lerp(light, clamp01((lum(col.getX(i), col.getY(i), col.getZ(i)) - lo) / (hi - lo)));
    col.setXYZ(i, c.r, c.g, c.b);
  }
  col.needsUpdate = true;
};

/** The alarm bell, its mouth down and its crown at the origin (it hangs from there). */
const bellGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  const prof = [
    [0.0, -0.01],
    [0.07, -0.02],
    [0.1, -0.08],
    [0.115, -0.17],
    [0.15, -0.25],
    [0.17, -0.28],
    [0.0, -0.28],
  ]
    .reverse()
    .map(([r, y]) => new THREE.Vector2((r as number) * 1.4, (y as number) * 1.4));
  // (Bottom to top, so the lathe's faces look outward.)
  kit.add(new THREE.LatheGeometry(prof, 12), '#8a909a', new THREE.Matrix4());
  kit.add(new THREE.TorusGeometry(0.217, 0.016, 4, 14).rotateX(Math.PI / 2), '#b4bac4', place(0, -0.35, 0));
  kit.add(new THREE.TorusGeometry(0.05, 0.016, 4, 8), IRON_DARK, place(0, 0.02, 0));
  kit.add(new THREE.SphereGeometry(0.055, 6, 4), '#2a2c31', place(0, -0.38, 0));
  const m = kit.build(new THREE.MeshBasicMaterial(), 'halls-bell') as THREE.Mesh;
  (m.material as THREE.Material).dispose();
  return m.geometry;
};

/** The lever's handle, pivoting at the origin about x, standing up along +y: an iron bar and a worn grip. */
const handleGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  kit.add(new THREE.BoxGeometry(0.04, 0.5, 0.04), IRON, place(0, 0.25, 0));
  kit.add(new THREE.CylinderGeometry(0.035, 0.035, 0.14, 6), '#3d2e25', place(0, 0.5, 0, 0, 1, 0, Math.PI / 2));
  kit.add(new THREE.SphereGeometry(0.05, 6, 5), IRON_DARK, place(0, 0, 0));
  const m = kit.build(new THREE.MeshBasicMaterial(), 'halls-lever') as THREE.Mesh;
  (m.material as THREE.Material).dispose();
  return m.geometry;
};

export class HallsLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private bells: THREE.InstancedMesh;
  private handles: THREE.InstancedMesh;
  private studs: THREE.InstancedMesh;
  private rings: THREE.InstancedMesh;
  private lamps: THREE.Vector3[] = [];
  /** Each guard's post on the floor, room by room (its ring's centre). */
  private posts: { room: number; at: THREE.Vector3 }[] = [];
  /** Per room: where its bell hangs, its lever's pivot and which way the lever faces, its studs' centre. */
  private rooms: { bell: THREE.Vector3; pivot: THREE.Vector3; yaw: number; lever: THREE.Vector3; swing: number; angle: number }[] = [];
  /** Where the Anvil's red comes from, far off past the rim. */
  private anvil = new THREE.Vector3();
  private now = 0;
  private last = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private v = new THREE.Vector3();
  private one = new THREE.Vector3(1, 1, 1);
  private sc = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
  ) {
    this.group.name = 'halls';
    const kit = new Kit();
    const glass = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const slab = new THREE.CylinderGeometry(0.62, 0.64, 0.05, 6);
    const pillar = new THREE.CylinderGeometry(0.13, 0.15, 1, 8);
    const rock = new THREE.DodecahedronGeometry(0.14, 0);
    const R = map.bastion;
    const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));

    // The floor: a flagstone on every hex of the clearing, a darker runner where the roads cross it.
    let n = 0;
    for (let q = -R; q <= R; q++)
      for (let rr = -R; rr <= R; rr++) {
        const h = { q, r: rr };
        if (hexDist(h, { q: 0, r: 0 }) > R) continue;
        const c = hexToWorld(q, rr);
        const p = ctx.at(c.x, c.y, 0);
        const road = onPath.has(`${q},${rr}`);
        const col = road ? RUNNER[n % RUNNER.length] : STONE[(n * 7 + q * 3) % STONE.length];
        n++;
        kit.add(slab, col as string, place(p.x, ctx.ground(p.x, p.z) + 0.005, p.z, ((n * 13) % 5 - 2) * 0.008));
        // A seam across the bigger slabs, so they read as laid stone.
        if (!road && n % 3 === 0) kit.add(box, GROUT, place(p.x, ctx.ground(p.x, p.z) + 0.032, p.z, (n % 6) * 0.52, new THREE.Vector3(0.012, 0.006, 0.9)));
      }

    // Round the edge: carved pillars, clear of the roads' mouths and the rooms, lamps on posts between them.
    const gates = map.sectors.map((sec) => {
      const c = hexToWorld(sec.gate.q, sec.gate.r);
      return Math.atan2(c.y, c.x);
    });
    const levers = r.rooms.map((room) => Math.atan2(room.lever.y, room.lever.x));
    const near = (a: number, list: number[], w: number): boolean => list.some((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) < w);
    const ER = (R + 0.75) * WORLD_PER_HEX;
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2 + 0.1;
      if (near(a, gates, 0.32) || near(a, levers, 0.42)) continue;
      const p = new THREE.Vector3(Math.cos(a) * ER, 0, Math.sin(a) * ER);
      p.y = ctx.ground(p.x, p.z);
      if (i % 2 === 0) this.pillar(kit, box, pillar, p, i);
      else this.lampPost(kit, glass, box, p, a + Math.PI, i);
    }

    // The rooms: an alcove past the rim, its alarm frame and bell, the lever, the guards' plinths.
    for (const [i, room] of r.rooms.entries()) {
      const lever = ctx.at(room.lever.x, room.lever.y, 0);
      const out = new THREE.Vector3(lever.x, 0, lever.z).normalize();
      const yaw = Math.atan2(out.x, out.z);
      const g = ctx.ground(lever.x, lever.z);
      // The alcove: a back wall of dressed blocks in an arc, two pillars at its mouth, a lintel cut with runes.
      const back = lever.clone().addScaledVector(out, 1.15);
      const m = place(back.x, ctx.ground(back.x, back.z), back.z, yaw);
      for (let k = -3; k <= 3; k++) {
        const a = k * 0.32;
        for (let row = 0; row < 3; row++)
          kit.add(box, row % 2 ? PILLAR_DARK : (STONE[(k + 4 + row) % STONE.length] as string), m, place(Math.sin(a) * 1.1, 0.22 + row * 0.42, -Math.cos(a) * 0.35 + 0.35, a, new THREE.Vector3(0.38, 0.4, 0.3)));
      }
      for (const sx of [-1, 1]) this.pillar(kit, box, pillar, new THREE.Vector3(back.x, 0, back.z).add(new THREE.Vector3(out.z, 0, -out.x).multiplyScalar(sx * 1.05)).setY(ctx.ground(back.x, back.z)), i * 5 + sx);
      kit.add(box, PILLAR, m, place(0, 1.42, 0.15, 0, new THREE.Vector3(2.3, 0.18, 0.32)));
      for (let k = 0; k < 5; k++) kit.add(box, RUNE, m, place(-0.4 + k * 0.2, 1.42, 0.32, 0, new THREE.Vector3(0.03, 0.1 + (k % 2) * 0.04, 0.01), 0, (k % 3 - 1) * 0.4));
      // The alarm frame: two iron-shod posts and a beam across the alcove's mouth, the bell from its middle.
      const fr = lever.clone().addScaledVector(out, 0.62);
      const fm = place(fr.x, ctx.ground(fr.x, fr.z), fr.z, yaw);
      for (const sx of [-1, 1]) {
        kit.add(box, WOOD[(i + sx + 3) % WOOD.length] as string, fm, place(sx * 0.42, 0.55, 0, 0, new THREE.Vector3(0.09, 1.1, 0.09)));
        kit.add(box, IRON_DARK, fm, place(sx * 0.42, 0.06, 0, 0, new THREE.Vector3(0.13, 0.12, 0.13)));
      }
      kit.add(box, WOOD[i % WOOD.length] as string, fm, place(0, 1.12, 0, 0, new THREE.Vector3(1.0, 0.1, 0.12)));
      kit.add(box, IRON_DARK, fm, place(0, 1.05, 0, 0, new THREE.Vector3(0.05, 0.06, 0.05)));
      const bell = new THREE.Vector3(0, 1.04, 0).applyMatrix4(fm);
      // The lever: an iron box on a stone step just in front of the frame, its handle on a pivot at its top.
      const lv = lever.clone().addScaledVector(out, 0.28);
      const lm = place(lv.x, ctx.ground(lv.x, lv.z), lv.z, yaw);
      kit.add(box, PILLAR_DARK, lm, place(0, 0.04, 0, 0, new THREE.Vector3(0.5, 0.08, 0.36)));
      kit.add(box, IRON_DARK, lm, place(0, 0.2, 0, 0, new THREE.Vector3(0.22, 0.24, 0.18)));
      kit.add(box, IRON, lm, place(0, 0.32, 0, 0, new THREE.Vector3(0.05, 0.03, 0.22)));
      const pivot = new THREE.Vector3(0, 0.33, 0).applyMatrix4(lm);
      // The guards' plinths: a round step of stone where each stands (and goes back to).
      for (const post of room.posts) {
        const pp = ctx.at(post.x, post.y, 0);
        kit.add(new THREE.CylinderGeometry(0.34, 0.38, 0.06, 10), PILLAR_DARK, place(pp.x, ctx.ground(pp.x, pp.z) + 0.02, pp.z));
        kit.add(new THREE.TorusGeometry(0.3, 0.015, 3, 16).rotateX(Math.PI / 2), RUNE, place(pp.x, ctx.ground(pp.x, pp.z) + 0.055, pp.z));
      }
      this.rooms.push({ bell, pivot, yaw, lever: new THREE.Vector3(lever.x, g + 0.02, lever.z), swing: 0, angle: UP });
    }

    // Cold dark rock beyond the edge, heaped behind the pillars.
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2;
      if (near(a, gates, 0.28)) continue;
      const rr = ER + 0.7 + (i % 3) * 0.35;
      const p = new THREE.Vector3(Math.cos(a) * rr, 0, Math.sin(a) * rr);
      kit.add(rock, ['#2b2c31', '#34353b', '#25262b'][i % 3] as string, place(p.x, ctx.ground(p.x, p.z) + 0.08, p.z, i * 1.3, 1.6 + (i % 4) * 0.5, i * 0.7));
    }

    const hallsMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.06, flatShading: true });
    const halls = kit.build(hallsMat, 'halls');
    this.mats.push(hallsMat);
    if (halls) {
      halls.receiveShadow = true;
      halls.castShadow = !ctx.low;
      this.geos.push(halls.geometry);
      this.group.add(halls);
    }
    const glassMat = new THREE.MeshBasicMaterial({ vertexColors: true });
    const lit = glass.build(glassMat, 'halls-lamps');
    this.mats.push(glassMat);
    if (lit) {
      this.geos.push(lit.geometry);
      this.group.add(lit);
    }

    const rooms = r.rooms.length;
    const bg = bellGeo();
    const hg = handleGeo();
    const sg = new THREE.CylinderGeometry(0.035, 0.04, 0.03, 6);
    const rg = new THREE.RingGeometry(0.92, 1, 40).rotateX(-Math.PI / 2);
    this.geos.push(bg, hg, sg, rg);
    const metalMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.45, metalness: 0.55, flatShading: true });
    const studMat = new THREE.MeshBasicMaterial({ color: '#ffffff' });
    const ringMat = new THREE.MeshBasicMaterial({ color: ALARM, transparent: true, opacity: 0.075, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mats.push(metalMat, studMat, ringMat);
    this.bells = new THREE.InstancedMesh(bg, metalMat, rooms);
    this.handles = new THREE.InstancedMesh(hg, metalMat, rooms);
    this.studs = new THREE.InstancedMesh(sg, studMat, rooms * STUDS);
    const posts = r.rooms.reduce((a, room) => a + room.posts.length, 0);
    this.rings = new THREE.InstancedMesh(rg, ringMat, Math.max(1, posts));
    this.rings.renderOrder = 1;
    for (const im of [this.bells, this.handles, this.studs, this.rings]) {
      im.frustumCulled = false;
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    this.bells.castShadow = !ctx.low;
    // The guards' reach on the floor: one ring round each post (its radius from the rules), hidden with the room.
    let k = 0;
    const rad = (r.aggro / 1000) * WORLD_PER_HEX;
    for (const room of r.rooms)
      for (const post of room.posts) {
        const pp = ctx.at(post.x, post.y, 0);
        pp.y = ctx.ground(pp.x, pp.z) + 0.04;
        this.posts.push({ room: r.rooms.indexOf(room), at: pp });
        this.m.compose(pp, this.q.identity(), new THREE.Vector3(rad, 1, rad));
        this.rings.setMatrixAt(k++, this.m);
      }
    this.rings.count = k;
    for (let i = 0; i < rooms * STUDS; i++) this.studs.setColorAt(i, STUD_OFF);
    this.group.add(this.bells, this.handles, this.studs, this.rings, this.glow.mesh);

    // The Anvil: off past the rim on the side away from the roads, its red on the rock.
    let best = 0;
    let bd = -1;
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const d = Math.min(...gates.map((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g)))), ...levers.map((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) + 0.4));
      if (d > bd) {
        bd = d;
        best = a;
      }
    }
    const ar = (map.radius - 1.2) * WORLD_PER_HEX;
    this.anvil.set(Math.cos(best) * ar, 0, Math.sin(best) * ar);
    this.anvil.y = ctx.ground(this.anvil.x, this.anvil.z);
    for (const gg of [box, slab, pillar, rock]) gg.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** A carved pillar: a square foot, a round shaft cut with a rune band, a capital. */
  private pillar(kit: Kit, box: THREE.BoxGeometry, shaft: THREE.CylinderGeometry, p: THREE.Vector3, i: number): void {
    const m = place(p.x, p.y, p.z, i * 0.7);
    kit.add(box, PILLAR_DARK, m, place(0, 0.1, 0, 0, new THREE.Vector3(0.42, 0.2, 0.42)));
    kit.add(shaft, PILLAR, m, place(0, 0.95, 0, 0, new THREE.Vector3(1, 1.5, 1)));
    kit.add(new THREE.TorusGeometry(0.16, 0.025, 4, 10).rotateX(Math.PI / 2), RUNE, m, place(0, 0.75, 0));
    kit.add(new THREE.TorusGeometry(0.16, 0.025, 4, 10).rotateX(Math.PI / 2), PILLAR_DARK, m, place(0, 1.2, 0));
    kit.add(box, PILLAR_DARK, m, place(0, 1.76, 0, 0, new THREE.Vector3(0.44, 0.14, 0.44)));
    kit.add(box, PILLAR, m, place(0, 1.88, 0, 0.785, new THREE.Vector3(0.36, 0.1, 0.36)));
  }

  /** A lamp on a post: a timber post, an arm, an iron cage with warm glass (its glow is drawn each frame). */
  private lampPost(kit: Kit, glass: Kit, box: THREE.BoxGeometry, p: THREE.Vector3, yaw: number, i: number): void {
    const m = place(p.x, p.y, p.z, yaw);
    kit.add(box, PILLAR_DARK, m, place(0, 0.05, 0, 0, new THREE.Vector3(0.24, 0.1, 0.24)));
    kit.add(box, WOOD[i % WOOD.length] as string, m, place(0, 0.62, 0, 0, new THREE.Vector3(0.09, 1.24, 0.09)));
    kit.add(box, WOOD[(i + 1) % WOOD.length] as string, m, place(0, 1.2, 0.14, 0, new THREE.Vector3(0.06, 0.06, 0.34)));
    const mm = m.clone().multiply(place(0, 1.02, 0.28));
    kit.add(box, IRON_DARK, mm, place(0, 0.13, 0, 0, new THREE.Vector3(0.16, 0.03, 0.16)));
    kit.add(box, IRON_DARK, mm, place(0, -0.09, 0, 0, new THREE.Vector3(0.14, 0.03, 0.14)));
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) kit.add(box, IRON_DARK, mm, place(x * 0.065, 0.02, z * 0.065, 0, new THREE.Vector3(0.015, 0.2, 0.015)));
    glass.add(new THREE.CylinderGeometry(0.05, 0.055, 0.15, 6), LAMP, mm, place(0, 0.02, 0));
    this.lamps.push(new THREE.Vector3(0, 0.02, 0).applyMatrix4(mm));
  }

  /** Re-dresses the board as the halls: cool stone, the woods to stone spires, dark overhead, the Anvil's red far off. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: AscuasSky | null, extra: THREE.Object3D[]): void {
    repaint(terrain.mesh.geometry, new THREE.Color('#1d2026'), new THREE.Color('#4e535e'), 0.03, 0.22);
    const c = new THREE.Color();
    const spire = [new THREE.Color('#2c2d33'), new THREE.Color('#484a53'), new THREE.Color('#3a3b42')];
    const rock = (o: THREE.Object3D): void => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        repaint(mesh.geometry, new THREE.Color('#0d0e11'), new THREE.Color('#30323a'), 0.008, 0.11);
        return;
      }
      if (o.name === 'roads') {
        repaint(mesh.geometry, new THREE.Color('#1f1c1a'), new THREE.Color('#4a433d'), 0.02, 0.14);
        return;
      }
      if (o.name === 'tufts') {
        o.visible = false;
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      if (mat.emissive && mat.emissive.getHex() !== 0) return;
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.instanceColor) {
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = clamp01(lum(c.r, c.g, c.b) / 0.12);
          c.copy(spire[i % 3] as THREE.Color).lerp(spire[1] as THREE.Color, l * 0.5);
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
        return;
      }
      if (mat.vertexColors || mat.map) return;
      const m2 = mat.clone();
      m2.color.lerp(spire[0] as THREE.Color, 0.7);
      mesh.material = m2;
      this.mats.push(m2);
    };
    terrain.decor.traverse(rock);
    for (const o of extra) o.traverse(rock);
    scene.background = new THREE.Color('#08090c');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#0d0e12');
    terrain.fog?.tint({ low: '#24120e', mid: '#121318', far: '#08090c', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#8a94b0');
      sky.hemi.groundColor.set('#32140c');
      sky.hemi.intensity *= 0.8;
      sky.key.color.set('#ece2d6');
      sky.key.intensity *= 0.75;
      sky.rim.color.set('#c8502e');
      sky.rim.intensity *= 0.55;
    }
  }

  /** Every fresh tick: a lever being pulled, one down, all of them; embers off the Anvil now and then. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    if (s.tick % (low ? 10 : 5) === 0) {
      const a = (s.tick * 0.37) % (Math.PI * 2);
      const p = this.anvil.clone().add(this.v.set(Math.cos(a) * 1.2, 0.3, Math.sin(a) * 1.2));
      this.ctx.vfx.burst(p, { n: 1, color: '#ff6a3a', speed: 0.01, up: 0.04, size: 0.06, life: 34, gravity: -0.0015, spread: 1.4 });
    }
    // The bells ring while their rooms are awake: a ripple off each now and then.
    const st = s.bells;
    if (s.started && !st?.done && s.tick % 18 === 0)
      this.rooms.forEach((room, i) => {
        if (st?.down[i]) return;
        this.ctx.vfx.ring(room.bell.clone().setY(room.bell.y - 0.2), 0.15, 0.9, '#ff8a6e', 12, 0.35);
      });
    for (const e of s._events ?? []) {
      if (e.t !== 'bells') continue;
      const room = this.rooms[e.room];
      if (e.kind === 'down' && room) {
        // The lever slams down: sparks off the box, the bell jerks and falls still, dust off the frame.
        this.ctx.flare(room.pivot.clone().setY(room.pivot.y + 0.2), '#ffd6b0', 2, 10);
        this.ctx.vfx.burst(room.pivot, { n: low ? 6 : 14, color: '#ffc27a', speed: 0.05, up: 0.04, size: 0.035, life: 10, gravity: 0.008, spread: 0.3 });
        this.ctx.vfx.burst(room.bell, { n: low ? 3 : 8, color: '#55545c', speed: 0.02, up: 0.02, size: 0.3, life: 20, smoke: true, spread: 0.5 });
        this.ctx.shake(0.12);
      } else if (e.kind === 'all') {
        for (const r of this.rooms) this.ctx.vfx.ring(r.lever, 0.2, 2.4, '#ffd6b0', 16, 0.5);
      }
    }
  }

  /** Every redraw: the bells swinging (or still), the levers, the pull's studs, the guards' rings, the glows. */
  draw(s: BattleState): void {
    const st = s.bells;
    const r = this.r;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    this.rooms.forEach((room, i) => {
      const down = !!st?.down[i];
      const held = down ? 1 : clamp01((st?.held[i] ?? 0) / r.pull);
      // The bell: a hard swing while the room is awake (it rings), dying away once it is still.
      const want = down || st?.done || !s.started ? 0 : 1;
      room.swing += (want - room.swing) * Math.min(1, dt * (want ? 3 : 1.2));
      const a = Math.sin(this.now * 7.5 + i * 1.9) * 0.55 * room.swing;
      this.q.setFromEuler(this.e.set(a, room.yaw, 0, 'YXZ'));
      this.m.compose(room.bell, this.q, this.one);
      this.bells.setMatrixAt(i, this.m);
      // The lever: up, pressed down as the pull fills, slammed home once down.
      const target = down ? DOWN : UP + (DOWN - UP) * held * 0.7;
      room.angle += (target - room.angle) * Math.min(1, dt * (down ? 14 : 8));
      this.q.setFromEuler(this.e.set(room.angle, room.yaw, 0, 'YXZ'));
      this.m.compose(room.pivot, this.q, this.one);
      this.handles.setMatrixAt(i, this.m);
      // The studs round its foot: lit one by one with the pull, all lit once down.
      for (let j = 0; j < STUDS; j++) {
        const t = (j / STUDS) * Math.PI * 2;
        const lit = j < Math.round(held * STUDS);
        this.m.compose(this.v.set(room.lever.x + Math.sin(t) * 0.5, room.lever.y + 0.01, room.lever.z + Math.cos(t) * 0.5), this.q.identity(), this.one);
        this.studs.setMatrixAt(i * STUDS + j, this.m);
        this.studs.setColorAt(i * STUDS + j, lit ? (down ? LAMP : STUD_ON) : STUD_OFF);
      }
    });
    // The guards' rings: shown while the room is awake (scaled to nothing once it is down).
    const rad = (r.aggro / 1000) * WORLD_PER_HEX;
    this.posts.forEach((p, i) => {
      const sc = st?.down[p.room] ? 0.0001 : rad * (1 + 0.02 * Math.sin(this.now * 3 + i));
      this.m.compose(p.at, this.q.identity(), this.sc.set(sc, 1, sc));
      this.rings.setMatrixAt(i, this.m);
    });
    this.bells.instanceMatrix.needsUpdate = true;
    this.handles.instanceMatrix.needsUpdate = true;
    this.studs.instanceMatrix.needsUpdate = true;
    if (this.studs.instanceColor) this.studs.instanceColor.needsUpdate = true;
    this.rings.instanceMatrix.needsUpdate = true;

    this.glow.begin();
    for (let i = 0; i < this.lamps.length; i++) {
      const p = this.lamps[i] as THREE.Vector3;
      const flick = 0.85 + 0.15 * Math.sin(this.now * 9 + i * 2.3) * Math.sin(this.now * 5.7 + i);
      this.glow.add(p.x, p.y, p.z, LAMP_CORE, 0.35, 0.8 * flick);
      this.glow.add(p.x, p.y, p.z, LAMP, 1.5, 0.3 * flick);
      this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, LAMP, 2.4, 0.07 * flick);
    }
    this.rooms.forEach((room, i) => {
      // An awake room's alarm: a red pulse under its bell; a still one, a dim warm lamp.
      const down = !!st?.down[i];
      if (!down && s.started && !st?.done) {
        const pulse = 0.5 + 0.5 * Math.sin(this.now * 7.5 + i * 1.9);
        this.glow.add(room.bell.x, room.bell.y - 0.15, room.bell.z, ALARM, 1.1, 0.18 + 0.2 * pulse);
      }
      const held = down ? 1 : clamp01((st?.held[i] ?? 0) / this.r.pull);
      if (held > 0 && !down) this.glow.add(room.lever.x, room.lever.y + 0.05, room.lever.z, this.c.copy(STUD_ON), 1.2 + held, 0.12 + 0.2 * held);
    });
    const beat = 0.85 + 0.15 * Math.sin(this.now * 1.1);
    this.glow.add(this.anvil.x, this.anvil.y + 0.4, this.anvil.z, ANVIL, 7, 0.16 * beat);
    this.glow.add(this.anvil.x, this.anvil.y + 0.2, this.anvil.z, ANVIL, 3, 0.22 * beat);
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    for (const im of [this.bells, this.handles, this.studs, this.rings]) im.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
